import { companyAccountListQuerySchema, type CompanyAccountListQuery, type Page } from '@chukua/common';
import { useEffect, useRef, useState } from 'react';
import { useListQuery } from '../../../shared/hooks/use-list-query';
import { toListParams } from '../../../shared/policies/list-params';
import { ApiError } from '../../../shared/shared.api';
import { listCompanyAccounts, type CompanyAccountView } from '../company-accounts.api';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type CompanyAccountsState =
  { status: 'loading' } | { status: 'loaded'; page: Page<CompanyAccountView>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: CompanyAccountListQuery) => toListParams(query).toString();

function toState(page: Page<CompanyAccountView>, query: CompanyAccountListQuery): CompanyAccountsState {
  return { status: 'loaded', page, queryKey: keyOf(query) };
}

function toErrorState(err: unknown): CompanyAccountsState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** The page of accounts the URL's list query asks for, re-fetched when it changes and on `reload` after a change. */
export function useCompanyAccountList() {
  const list = useListQuery(companyAccountListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<CompanyAccountsState>({ status: 'loading' });
  // Only the latest request may write state: a slow earlier response must not overwrite a newer one.
  const latestRequest = useRef(0);

  useEffect(() => {
    const request = ++latestRequest.current;

    listCompanyAccounts(query)
      .then((page) => {
        if (request === latestRequest.current) setState(toState(page, query));
      })
      .catch((err) => {
        if (request === latestRequest.current) setState(toErrorState(err));
      });
  }, [query]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(query);

  /** Re-fetches the current page after a change, so the row shows its new state. The change is already saved, so a failure keeps the old rows. */
  async function reload(): Promise<void> {
    const request = ++latestRequest.current;
    try {
      const page = await listCompanyAccounts(query);
      if (request === latestRequest.current) setState(toState(page, query));
    } catch {
      // The old rows stay up; reloading the page shows the truth.
    }
  }

  return { state, list, isRefreshing, reload };
}
